import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, documentId, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../shared/storage/firebase';
import { Squad } from '../../../types';
import { createDateInZone, DEFAULT_TZ, formatChicagoTimeOnly, formatTimeInZone, getTimeZoneLabel, toChicagoTime } from '../../../shared/utils/timezone';
import '../styles/TeacherDashboard.css';
import { useAuth } from '../../auth/contexts/AuthContext';
import { addDays } from 'date-fns';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import ConfirmationModal from './ConfirmationModal';
import RequiredTeacherLogisticsModal from './RequiredTeacherLogisticsModal';
import TeacherCalendarSubscription from './TeacherCalendarSubscription';
import {
  cancelSubRequestForSquadEvent,
  claimSubOpportunity,
  dateKeyForDateInZone,
  quarterKeyForDateInZone,
  requestSubForSquadEvent,
  type SubRequestReason,
  type SubStatus,
} from '../services/subbing';
import { resolveEffectiveEventStaffing, type ActiveCoverage } from '../utils/effectiveEventStaffing';

type SupportedState = 'Massachusetts' | 'Illinois';

function parseSupportedStateFromAddress(address: string | null | undefined): SupportedState | null {
  if (!address) return null;
  const normalized = String(address).trim();
  if (!normalized) return null;

  if (/\b(Massachusetts|MA)\b/i.test(normalized)) return 'Massachusetts';
  if (/\b(Illinois|IL)\b/i.test(normalized)) return 'Illinois';
  return null;
}

function dateRangesOverlap(aStart: Date, aEnd: Date, bStart: Date, bEnd: Date): boolean {
  const as = aStart?.getTime?.() ?? NaN;
  const ae = aEnd?.getTime?.() ?? NaN;
  const bs = bStart?.getTime?.() ?? NaN;
  const be = bEnd?.getTime?.() ?? NaN;
  if (![as, ae, bs, be].every(Number.isFinite)) return false;
  return as < be && bs < ae;
}

type SquadCalendarEvent = {
  id: string;
  title: string;
  start: Date;
  end: Date;
  squadId: string;
  mainTeacherId?: string;
  supportingTeacherIds?: string[];
  subStatus?: SubStatus;
  subRequestedByTeacherId?: string | null;
  subRequestedByTeacherName?: string | null;
  subAssignedTeacherId?: string | null;
  activeCoverage?: ActiveCoverage[];
};

type SubOpportunityEvent = {
  id: string;
  title: string;
  start: Date;
  end: Date;
  squadId: string;
  mainTeacherId?: string | null;
  supportingTeacherIds?: string[];
  subStatus?: SubStatus;
  subRequestedByTeacherId?: string | null;
  subRequestedByTeacherName?: string | null;
  activeCoverage?: ActiveCoverage[];
};

function isSameChicagoDay(a: Date, b: Date): boolean {
  const ca = toChicagoTime(a);
  const cb = toChicagoTime(b);
  return (
    ca.getFullYear() === cb.getFullYear()
    && ca.getMonth() === cb.getMonth()
    && ca.getDate() === cb.getDate()
  );
}

function dayKeyInZone(d: Date, timeZone?: string | null): string {
  const tz = (timeZone || DEFAULT_TZ).toString();
  return formatTimeInZone(d, tz, 'yyyy-MM-dd');
}

function formatRelativeDayLabel(date: Date): string {
  const now = new Date();
  if (isSameChicagoDay(date, now)) return 'Today';
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  if (isSameChicagoDay(date, tomorrow)) return 'Tomorrow';
  return formatChicagoTimeOnly(date, 'EEE'); // Mon/Tue/...
}

function formatUrgency(start: Date, now: Date, timeZone?: string | null): string {
  const tz = (timeZone || DEFAULT_TZ).toString();
  const short = tzAbbrev(tz);
  const diffMs = start.getTime() - now.getTime();
  if (diffMs <= 2 * 60 * 1000) return 'Starting now';

  // If the class is tomorrow (in the squad's timezone), show a concrete anchor.
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  if (dayKeyInZone(start, tz) === dayKeyInZone(tomorrow, tz)) {
    return `Tomorrow at ${formatTimeInZone(start, tz, 'h:mm a')} ${short}`;
  }

  // For same-day (in the squad's timezone): "Starts in 42 minutes" / "Starts in 2h 15m"
  if (dayKeyInZone(start, tz) === dayKeyInZone(now, tz)) {
    const totalMinutes = Math.max(1, Math.round(diffMs / 60000));
    if (totalMinutes < 60) return `Starts in ${totalMinutes} minutes`;
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    if (hours <= 5 && mins > 0) return `Starts in ${hours}h ${mins}m`;
    return `Starts in ${hours} hours`;
  }

  // Within the next week: "Tue at 3:30 PM"
  const withinWeekMs = 7 * 24 * 60 * 60 * 1000;
  if (diffMs < withinWeekMs) {
    return `${formatTimeInZone(start, tz, 'EEE')} at ${formatTimeInZone(start, tz, 'h:mm a')} ${short}`;
  }

  // Fallback: concrete date
  return `${formatTimeInZone(start, tz, 'MMM d')} at ${formatTimeInZone(start, tz, 'h:mm a')} ${short}`;
}

const SCHEDULE_TZ = DEFAULT_TZ;

function tzAbbrev(timeZone?: string | null): 'CT' | 'ET' {
  const tz = (timeZone || DEFAULT_TZ).toLowerCase();
  if (tz.includes('new_york')) return 'ET';
  return 'CT';
}

const SUB_OPP_LOOKAHEAD_DAYS = 365;
const SUB_OPP_QUERY_LIMIT = 500;

function subjectLabelFromSquad(s: Squad | null): string {
  const raw = (s?.curriculumId || 'music').toString().trim();
  if (!raw) return 'Music';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function enrollmentMessage(squad: Squad | null): { text: string; tone: 'pending' | 'under' | 'at' | 'over' | 'neutral' } | null {
  if (!squad) return null;
  const mode = String((squad as any).paymentMode || 'stripe').trim().toLowerCase();
  const studentIds = Array.isArray(squad.studentIds) ? squad.studentIds : [];
  const seatsTaken = Number((squad as any).seatsTaken);
  const enrollment = Number.isFinite(seatsTaken) ? Math.max(seatsTaken, studentIds.length) : studentIds.length;
  const minimumRaw = Number((squad as any).minEnroll);
  const minimum = Number.isFinite(minimumRaw) && minimumRaw > 0 ? minimumRaw : null;

  if (enrollment === 0) {
    return {
      text: mode === 'stripe'
        ? 'Enrollment pending marketing push'
        : 'Enrollment pending partner organization telling us',
      tone: 'pending',
    };
  }
  if (!minimum) return { text: `Current enrollment: ${enrollment}`, tone: 'neutral' };
  if (enrollment < minimum) return { text: `Current enrollment: ${enrollment} · Under minimum to run (${minimum})`, tone: 'under' };
  if (enrollment === minimum) return { text: `Current enrollment: ${enrollment} · At minimum to run`, tone: 'at' };
  return { text: `Current enrollment: ${enrollment} · Over minimum to run (${minimum})`, tone: 'over' };
}

function compactEnrollmentMessage(squad: Squad | null): string {
  if (!squad) return '';
  const studentIds = Array.isArray(squad.studentIds) ? squad.studentIds : [];
  const seatsTaken = Number((squad as any).seatsTaken);
  const enrollment = Number.isFinite(seatsTaken) ? Math.max(seatsTaken, studentIds.length) : studentIds.length;
  const minimumRaw = Number((squad as any).minEnroll);
  const minimum = Number.isFinite(minimumRaw) && minimumRaw > 0 ? minimumRaw : null;
  if (enrollment === 0) return '0 enrolled · Pending';
  if (!minimum) return `${enrollment} enrolled`;
  if (enrollment < minimum) return `${enrollment} enrolled · Under ${minimum}`;
  if (enrollment === minimum) return `${enrollment} enrolled · At minimum`;
  return `${enrollment} enrolled · Over ${minimum}`;
}

function subUrgencyBadge(start: Date, now: Date, timeZone?: string | null): 'Today' | 'Tomorrow' | 'This week' | null {
  const tz = (timeZone || DEFAULT_TZ).toString();
  if (dayKeyInZone(start, tz) === dayKeyInZone(now, tz)) return 'Today';
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  if (dayKeyInZone(start, tz) === dayKeyInZone(tomorrow, tz)) return 'Tomorrow';
  const withinWeekMs = 7 * 24 * 60 * 60 * 1000;
  if (start.getTime() - now.getTime() < withinWeekMs) return 'This week';
  return null;
}

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function chicagoDayKey(d: Date): string {
  return formatTimeInZone(d, SCHEDULE_TZ, 'yyyy-MM-dd');
}

function chicagoWeekdayLabel(d: Date): string {
  return formatTimeInZone(d, SCHEDULE_TZ, 'EEE');
}

function chicagoSundayIndex(d: Date): number {
  // ISO day of week: 1 (Mon) .. 7 (Sun). Convert to 0 (Sun) .. 6 (Sat).
  const iso = Number(formatTimeInZone(d, SCHEDULE_TZ, 'i'));
  return Number.isFinite(iso) ? (iso % 7) : 0;
}

function fullCalendarWallTime(d: Date, timeZone: string): string {
  return formatTimeInZone(d, timeZone, "yyyy-MM-dd'T'HH:mm:ss");
}

const TeacherDashboard: React.FC<{ squads: Squad[] }> = ({ squads }) => {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const [events, setEvents] = useState<SquadCalendarEvent[]>([]);
  const [teacherNamesById, setTeacherNamesById] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [weekOffset, setWeekOffset] = useState(0); // 0 = current week, 1 = next week, etc.
  const [subOppEvents, setSubOppEvents] = useState<SubOpportunityEvent[]>([]);
  const [subOppSquads, setSubOppSquads] = useState<Record<string, Squad>>({});
  const [locationsById, setLocationsById] = useState<Record<string, any>>({});
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [weekEventModalOpen, setWeekEventModalOpen] = useState(false);
  const [weekEventModalMode, setWeekEventModalMode] = useState<'actions' | 'requestSub'>('actions');
  const [selectedWeekEvent, setSelectedWeekEvent] = useState<SquadCalendarEvent | null>(null);
  const [logisticsModalOpen, setLogisticsModalOpen] = useState(false);
  const [subReason, setSubReason] = useState<SubRequestReason>('schedule_conflict');
  const [subNote, setSubNote] = useState<string>('');
  const [subRequestedRole, setSubRequestedRole] = useState<'lead' | 'co_teacher'>('lead');
  const [subNoteError, setSubNoteError] = useState<string | null>(null);
  const [requestingSub, setRequestingSub] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getDocs(collection(db, 'locations'))
      .then((snapshot) => {
        if (cancelled) return;
        setLocationsById(Object.fromEntries(
          snapshot.docs.map((locationDoc) => [
            locationDoc.id,
            { id: locationDoc.id, ...locationDoc.data() },
          ]),
        ));
      })
      .catch((error) => console.error('Failed to load canonical locations', error));
    return () => { cancelled = true; };
  }, []);
  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmLabel?: string;
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
    confirmLabel: undefined,
  });

  useEffect(() => {
    const load = async () => {
      if (!currentUser?.uid) {
        setEvents([]);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        // Fetch events the teacher is assigned to (main or supporting).
        const [byMain, bySupport, byRequested, bySubstitute, byParticipant] = await Promise.all([
          getDocs(query(collection(db, 'squadEvents'), where('mainTeacherId', '==', currentUser.uid))),
          getDocs(query(collection(db, 'squadEvents'), where('supportingTeacherIds', 'array-contains', currentUser.uid))),
          // Keep open requests visible to their requester even if an automatic
          // assignment workflow has not left them in main/supportingTeacherIds.
          getDocs(query(collection(db, 'squadEvents'), where('subRequestedByTeacherId', '==', currentUser.uid))),
          getDocs(query(collection(db, 'squadEvents'), where('subAssignedTeacherId', '==', currentUser.uid))),
          getDocs(query(collection(db, 'squadEvents'), where('eventParticipantIds', 'array-contains', currentUser.uid))),
        ]);

        const map = new Map<string, SquadCalendarEvent>();
        const pushDocs = (snap: any) => {
          snap.docs.forEach((docSnap: any) => {
            const data = docSnap.data();
            const ev: SquadCalendarEvent = {
              id: docSnap.id,
              title: data.title,
              start: data.start?.toDate?.() || new Date(0),
              end: data.end?.toDate?.() || new Date(0),
              squadId: data.squadId,
              mainTeacherId: data.mainTeacherId,
              supportingTeacherIds: data.supportingTeacherIds || [],
              subStatus: (data.subStatus || 'none') as SubStatus,
              subRequestedByTeacherId: (typeof data.subRequestedByTeacherId === 'string' ? data.subRequestedByTeacherId : (data.subRequestedByTeacherId ?? null)),
              subRequestedByTeacherName: (typeof data.subRequestedByTeacherName === 'string' ? data.subRequestedByTeacherName : (data.subRequestedByTeacherName ?? null)),
              subAssignedTeacherId: (typeof data.subAssignedTeacherId === 'string' ? data.subAssignedTeacherId : (data.subAssignedTeacherId ?? null)),
              activeCoverage: Array.isArray(data.activeCoverage) ? data.activeCoverage : undefined,
            };
            map.set(ev.id, ev);
          });
        };
        pushDocs(byMain);
        pushDocs(bySupport);
        pushDocs(byRequested);
        pushDocs(bySubstitute);
        pushDocs(byParticipant);
        setEvents(Array.from(map.values()));
      } catch (e) {
        console.error('Failed to load teacher schedule events', e);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser?.uid]);

  useEffect(() => {
    let cancelled = false;
    const assignedTeacherIds = Array.from(new Set(
      events
        .flatMap((event) => {
          const ids: string[] = [];
          // Requester's view: "X is subbing for you" needs the substitute's name.
          if (event.subStatus === 'assigned' && event.subRequestedByTeacherId === currentUser?.uid) {
            ids.push(String(event.subAssignedTeacherId || event.mainTeacherId || '').trim());
          }
          // Substitute's view: "Covering <role> for Y" needs the absent teacher's name.
          const coverage = event.activeCoverage?.[0];
          if (
            coverage
            && String(event.subAssignedTeacherId || coverage.substituteTeacherId || '').trim() === currentUser?.uid
          ) {
            ids.push(String(coverage.requestedTeacherId || '').trim());
          }
          return ids;
        })
        .filter(Boolean),
    ));

    if (assignedTeacherIds.length === 0) {
      setTeacherNamesById({});
      return () => { cancelled = true; };
    }

    const loadTeacherNames = async () => {
      try {
        const names: Record<string, string> = {};
        for (const ids of chunk(assignedTeacherIds, 10)) {
          const snapshot = await getDocs(query(collection(db, 'users'), where(documentId(), 'in', ids)));
          snapshot.docs.forEach((teacherDoc) => {
            const data: any = teacherDoc.data();
            const fullName = [data.firstName, data.lastName].map((part: unknown) => String(part || '').trim()).filter(Boolean).join(' ');
            names[teacherDoc.id] = String(data.displayName || fullName || data.email || 'Your substitute').trim();
          });
        }
        if (!cancelled) setTeacherNamesById(names);
      } catch (error) {
        console.warn('Failed to load substitute teacher names', error);
        if (!cancelled) setTeacherNamesById({});
      }
    };

    loadTeacherNames();
    return () => { cancelled = true; };
  }, [events, currentUser?.uid]);

  // Load unassigned classes for the current season (subbing opportunities).

  // Keep countdowns fresh (no backend traffic; just time math)
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const squadById = useMemo(() => {
    const m = new Map<string, Squad>();
    squads.forEach(s => m.set(s.id, s));
    return m;
  }, [squads]);

  const now = useMemo(() => new Date(nowMs), [nowMs]);
  const teacherState = useMemo(() => {
    const shipState = String((userData as any)?.shippingAddress?.state || '').trim();
    if (shipState) return parseSupportedStateFromAddress(shipState);
    const neighborhood = String((userData as any)?.neighborhood || '').trim();
    const legacyAddr = String((userData as any)?.address || '').trim();
    return parseSupportedStateFromAddress(neighborhood || legacyAddr);
  }, [userData]);

  const teacherTz = useMemo(() => {
    const tz = String((userData as any)?.timeZone || (userData as any)?.timezone || DEFAULT_TZ).trim();
    return tz || DEFAULT_TZ;
  }, [userData]);
  const teacherTzLabel = useMemo(() => getTimeZoneLabel(teacherTz), [teacherTz]);

  const calendarDayKey = useCallback((d: Date): string => {
    return dayKeyInZone(d, teacherTz);
  }, [teacherTz]);

  const finalDayKeyBySquadId = useMemo(() => {
    const map: Record<string, string> = {};

    const isoDowFromSchedule = (raw: any): number | null => {
      if (raw === null || raw === undefined) return null;
      // Accept numeric day indices: 0(Sun)..6(Sat) OR 1(Mon)..7(Sun)
      if (typeof raw === 'number' && Number.isFinite(raw)) {
        const n = Math.trunc(raw);
        if (n >= 1 && n <= 7) return n;
        if (n >= 0 && n <= 6) return n === 0 ? 7 : n;
      }
      const s = String(raw || '').trim().toLowerCase();
      if (!s) return null;
      const map3: Record<string, number> = {
        mon: 1, tue: 2, tues: 2, wed: 3, thu: 4, thur: 4, thurs: 4, fri: 5, sat: 6, sun: 7,
      };
      if (s in map3) return map3[s];
      const mapLong: Record<string, number> = {
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6,
        sunday: 7,
      };
      if (s in mapLong) return mapLong[s];
      const mapLegacyCodes: Record<string, number> = {
        mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6, sun: 7,
      };
      // Support stored codes like "MON"
      const upper3 = s.slice(0, 3);
      if (upper3 in mapLegacyCodes) return mapLegacyCodes[upper3];
      return null;
    };

    const ymdKeyForSquadDate = (d: Date, tz: string) => formatTimeInZone(d, tz, 'yyyy-MM-dd');

    const computeFinalYmdFromSchedule = (squad: Squad | null): string | null => {
      if (!squad) return null;
      const tz = String((squad as any)?.timezone || DEFAULT_TZ).trim() || DEFAULT_TZ;
      const sched: any = (squad as any)?.schedule || null;
      const mainDate = String(sched?.mainDate || '').trim();
      if (mainDate) return mainDate;
      const startYmd = String(sched?.startDate || '').trim();
      const endYmd = String(sched?.endDate || '').trim();
      const targetIso = isoDowFromSchedule(sched?.dayOfWeek);
      if (!startYmd || !endYmd || !targetIso) return null;

      let start = createDateInZone(startYmd, '12:00', tz);
      let end = createDateInZone(endYmd, '12:00', tz);
      if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
      if (start.getTime() > end.getTime()) {
        const tmp = start;
        start = end;
        end = tmp;
      }

      const exceptions = new Set(
        (Array.isArray(sched?.exceptions) ? sched.exceptions : [])
          .map((x: any) => String(x || '').trim())
          .filter(Boolean)
      );
      const makeups = (Array.isArray(sched?.makeups) ? sched.makeups : [])
        .map((x: any) => String(x || '').trim())
        .filter(Boolean);

      const startIso = Number(formatTimeInZone(start, tz, 'i')); // 1..7
      const delta = ((targetIso - startIso) + 7) % 7;
      let d = addDays(start, delta);

      let last: string | null = null;
      // Regular weekly occurrences (excluding exceptions)
      for (; d.getTime() <= end.getTime(); d = addDays(d, 7)) {
        const ymd = ymdKeyForSquadDate(d, tz);
        if (!ymd) continue;
        if (exceptions.has(ymd)) continue;
        if (!last || ymd > last) last = ymd;
      }

      // Makeups are additional class days (count even if outside endDate)
      for (const ymd of makeups) {
        if (!last || ymd > last) last = ymd;
      }

      return last;
    };

    for (const s of squads) {
      const sid = String((s as any)?.id || '').trim();
      if (!sid) continue;
      const last = computeFinalYmdFromSchedule(s);
      if (last) map[sid] = last;
    }
    return map;
  }, [squads]);

  const finalStartMsBySquadId = useMemo(() => {
    // Derived from squad schedule final day key (noon in squad timezone).
    const map: Record<string, number> = {};
    for (const s of squads) {
      const sid = String((s as any)?.id || '').trim();
      if (!sid) continue;
      const tz = String((s as any)?.timezone || DEFAULT_TZ).trim() || DEFAULT_TZ;
      const lastKey = finalDayKeyBySquadId[sid];
      if (!lastKey) continue;
      const d = createDateInZone(lastKey, '12:00', tz);
      const t = d?.getTime?.();
      if (typeof t === 'number' && Number.isFinite(t)) map[sid] = t;
    }
    return map;
  }, [squads, finalDayKeyBySquadId]);

  useEffect(() => {
    const loadSubOpps = async () => {
      try {
        setLoadingSubs(true);
        const start = new Date(now.getTime() - 5 * 60 * 1000); // allow just-started
        const endInclusive = addDays(start, SUB_OPP_LOOKAHEAD_DAYS);

        // Pull upcoming classes and treat:
        // - legacy unassigned events (no mainTeacherId) OR
        // - explicit sub-requested events (subStatus === 'requested')
        // as subbing opportunities.
        const snap = await getDocs(query(
          collection(db, 'squadEvents'),
          where('start', '>=', start),
          where('start', '<=', endInclusive),
          orderBy('start', 'asc'),
          limit(SUB_OPP_QUERY_LIMIT),
        ));

        const rows: SubOpportunityEvent[] = snap.docs.map((docSnap) => {
          const data: any = docSnap.data();
          return {
            id: docSnap.id,
            title: data.title,
            start: data.start?.toDate?.() || new Date(0),
            end: data.end?.toDate?.() || new Date(0),
            squadId: data.squadId,
            mainTeacherId: (typeof data.mainTeacherId === 'string' ? data.mainTeacherId : (data.mainTeacherId ?? null)),
            supportingTeacherIds: data.supportingTeacherIds || [],
            subStatus: (data.subStatus || 'none') as SubStatus,
            subRequestedByTeacherId: (typeof data.subRequestedByTeacherId === 'string' ? data.subRequestedByTeacherId : (data.subRequestedByTeacherId ?? null)),
            subRequestedByTeacherName: (typeof data.subRequestedByTeacherName === 'string' ? data.subRequestedByTeacherName : (data.subRequestedByTeacherName ?? null)),
            activeCoverage: Array.isArray(data.activeCoverage) ? data.activeCoverage : undefined,
          };
        });

        const opps = rows
          .filter(e => e.start && !Number.isNaN(e.start.getTime()))
          .filter(e => e.start.getTime() >= now.getTime() - 5 * 60 * 1000) // allow just-started (client safety)
          // Subbing opportunities include legacy unassigned events AND sub-requested events
          .filter(e => !e.mainTeacherId || e.subStatus === 'requested')
          // Don't show your own sub requests back to you
          .filter(e => !(e.subStatus === 'requested' && e.subRequestedByTeacherId && e.subRequestedByTeacherId === currentUser?.uid))
          .filter(e => !!e.squadId);

        setSubOppEvents(opps);

        // Fetch squads for any event squads we don't already have via props.
        const ids = Array.from(new Set(opps.map(e => String(e.squadId)).filter(Boolean)));
        const missing = ids.filter(id => !squadById.has(id));
        if (missing.length === 0) {
          setSubOppSquads({});
          return;
        }

        const fetched: Record<string, Squad> = {};
        for (const group of chunk(missing, 10)) {
          const sSnap = await getDocs(query(collection(db, 'squads'), where(documentId(), 'in', group)));
          sSnap.docs.forEach(d => {
            fetched[d.id] = { ...(d.data() as any), id: d.id } as Squad;
          });
        }
        setSubOppSquads(fetched);
      } catch (e) {
        console.error('Failed to load subbing opportunities', e);
        setSubOppEvents([]);
        setSubOppSquads({});
      } finally {
        setLoadingSubs(false);
      }
    };
    loadSubOpps();
  }, [nowMs, squadById, now, currentUser?.uid]);

  const upcoming = useMemo(() => {
    return events
      .filter(e => e.start && !Number.isNaN(e.start.getTime()))
      .filter(e => !(e.subStatus === 'assigned' && e.subRequestedByTeacherId === currentUser?.uid))
      .filter(e => e.start.getTime() >= now.getTime() - 5 * 60 * 1000) // allow just-started
      .sort((a, b) => a.start.getTime() - b.start.getTime())
      .slice(0, 6);
  }, [events, nowMs, currentUser?.uid]);

  const nextClass = upcoming[0] || null;
  const nextSquad = nextClass ? squadById.get(nextClass.squadId) || null : null;
  const nextEnrollment = useMemo(() => enrollmentMessage(nextSquad), [nextSquad]);
  const nextTz = (nextSquad?.timezone || DEFAULT_TZ).toString();
  const nextTzShort = tzAbbrev(nextTz);
  const nextFinalStart = useMemo(() => {
    if (!nextClass) return null;
    const t = finalStartMsBySquadId[String(nextClass.squadId)] ?? null;
    return (typeof t === 'number' && Number.isFinite(t)) ? new Date(t) : null;
  }, [nextClass, finalStartMsBySquadId]);
  const nextFinalKey = useMemo(() => {
    if (!nextFinalStart) return null;
    return dayKeyInZone(nextFinalStart, nextTz);
  }, [nextFinalStart, nextTz]);
  const nextClassKey = useMemo(() => {
    if (!nextClass) return null;
    return dayKeyInZone(nextClass.start, nextTz);
  }, [nextClass, nextTz]);
  const isFinalDayNextClass = useMemo(() => {
    if (!nextClassKey || !nextFinalKey) return false;
    return nextClassKey === nextFinalKey;
  }, [nextClassKey, nextFinalKey]);
  const weeksLeft = useMemo(() => {
    if (!nextClassKey || !nextFinalKey) return null;
    const a = createDateInZone(nextClassKey, '12:00', nextTz);
    const b = createDateInZone(nextFinalKey, '12:00', nextTz);
    const diffDays = Math.round((b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000));
    if (!Number.isFinite(diffDays) || diffDays < 0) return 0;
    // Inclusive: any date within the final 7 days shows "1 week left"
    return Math.max(1, Math.ceil((diffDays + 1) / 7));
  }, [nextClassKey, nextFinalKey, nextTz]);
  const isFinalWeekNextClass = useMemo(() => {
    return weeksLeft === 1;
  }, [weeksLeft]);
  const firstName = useMemo(() => {
    const name = (userData?.displayName || '').trim();
    if (!name) return 'there';
    return name.split(/\s+/)[0] || 'there';
  }, [userData?.displayName]);
  const nextClassPhrase = useMemo(() => {
    if (!nextClass) return '';
    return formatUrgency(nextClass.start, now, nextTz);
  }, [nextClass, nowMs, nextTz]);

  const currentAvailabilityQuarterKey = useMemo(() => {
    return quarterKeyForDateInZone(now, teacherTz);
  }, [nowMs, now, teacherTz]);

  const currentAvailabilityQuarterLabel = useMemo(() => {
    const labels: Record<ReturnType<typeof quarterKeyForDateInZone>, string> = {
      winter2026: 'Winter',
      spring2026: 'Spring',
      summer2026: 'Summer',
      fall2026: 'Fall',
      winter2027: 'Winter',
      spring2027: 'Spring',
    };
    return labels[currentAvailabilityQuarterKey] || 'current season';
  }, [currentAvailabilityQuarterKey]);

  const needsCurrentSeasonWeeklyAvailability = useMemo(() => {
    const incoming = ((userData as any)?.availabilityTemplates || {}) as any;
    return !incoming?.[currentAvailabilityQuarterKey]?.week;
  }, [currentAvailabilityQuarterKey, userData]);

  const baseWeekStart = useMemo(() => {
    // Chicago Sunday 00:00 of the current week.
    const dow = chicagoSundayIndex(now);
    const sunday = addDays(now, -dow);
    const sundayStr = formatTimeInZone(sunday, SCHEDULE_TZ, 'yyyy-MM-dd');
    return createDateInZone(sundayStr, '00:00', SCHEDULE_TZ);
  }, [nowMs]);

  const weekStart = useMemo(() => addDays(baseWeekStart, weekOffset * 7), [baseWeekStart, weekOffset]);

  const weekEndExclusive = useMemo(() => addDays(weekStart, 7), [weekStart]);
  const weekEndInclusive = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const weekLabel = useMemo(() => {
    if (weekOffset === 0) return 'This week';
    const a = formatTimeInZone(weekStart, SCHEDULE_TZ, 'MMM d');
    const b = formatTimeInZone(weekEndInclusive, SCHEDULE_TZ, 'MMM d');
    return `${a}–${b}`;
  }, [weekOffset, weekStart, weekEndInclusive]);

  const thisWeek = useMemo(() => {
    const startMs = weekStart.getTime();
    const endMs = weekEndExclusive.getTime();
    return events
      .filter(e => e.start && !Number.isNaN(e.start.getTime()))
      .filter(e => {
        const t = e.start.getTime();
        return t >= startMs && t < endMs;
      })
      .sort((a, b) => a.start.getTime() - b.start.getTime());
  }, [events, weekStart, weekEndExclusive]);

  const groupedWeek = useMemo(() => {
    const groups: Array<{ dayKey: string; dayLabel: string; items: SquadCalendarEvent[] }> = [];
    for (let i = 0; i < 7; i++) {
      const dayStart = addDays(weekStart, i);
      groups.push({
        dayKey: chicagoDayKey(dayStart),
        dayLabel: chicagoWeekdayLabel(dayStart),
        items: [],
      });
    }
    const byKey = new Map(groups.map(g => [g.dayKey, g]));
    for (const ev of thisWeek) {
      const key = chicagoDayKey(ev.start);
      const g = byKey.get(key);
      if (g) g.items.push(ev);
    }
    return groups;
  }, [thisWeek, weekStart]);

  const availableSubOpps = useMemo(() => {
    return subOppEvents
      .filter(e => e.start && !Number.isNaN(e.start.getTime()))
      .filter(e => {
        // Don't show "subbing opportunities" for squads that aren't staffed yet.
        // (If the squad doesn't have a lead teacher selected in its details, it's not a real coverage need.)
        const s = e.squadId
          ? (squadById.get(e.squadId) || subOppSquads[String(e.squadId)] || null)
          : null;
        if (!s) return false;
        const inactiveStatuses = ['archived', 'cancelled', 'canceled', 'closed', 'inactive'];
        const lifecycles = [(s as any)?.status, (s as any)?.classStatus]
          .map((value) => String(value || '').trim().toLowerCase());
        if (lifecycles.some((value) => inactiveStatuses.includes(value))) return false;
        const isPublishedDemo = (s as any)?.status === 'published'
          && (s as any)?.scheduleKind === 'single_day'
          && (s as any)?.singleDayType === 'demo';
        if (isPublishedDemo && !e.mainTeacherId) return true;
        // An explicit sub request proves this is a staffed class even when the
        // squad's legacy regularTeacher/leadTeacherId fields were never backfilled.
        if (e.subStatus === 'requested') return true;
        const leadId = String((s as any)?.regularTeacher || (s as any)?.leadTeacherId || '').trim();
        return leadId.length > 0;
      })
      .filter(e => {
        // Only show opportunities in the same state as the teacher (when known).
        if (!teacherState) return true;
        const s = e.squadId
          ? (squadById.get(e.squadId) || subOppSquads[String(e.squadId)] || null)
          : null;
        const locationId = String(s?.locationId || '').trim();
        const canonicalLocation = locationId ? locationsById[locationId] : null;
        const addr = (
          canonicalLocation?.address
          || canonicalLocation?.location?.address
          || s?.location?.address
          || s?.address
          || ''
        ) as any;
        const state = parseSupportedStateFromAddress(addr);
        // If we can't determine the squad's state from its address, do NOT hide the opportunity.
        // (Many squads don't store a normalized address string.)
        if (!state) return true;
        return state === teacherState;
      })
      .sort((a, b) => a.start.getTime() - b.start.getTime());
  }, [subOppEvents, teacherState, squadById, subOppSquads, locationsById]);

  const canTeacherClaimOpportunity = useCallback((ev: SubOpportunityEvent): boolean => {
    if (!currentUser?.uid) return false;
    const uid = String(currentUser.uid);
    // Requirement: only hide Claim if it overlaps another class. An event never
    // blocks claiming itself (the stored teacher may claim their class back from
    // a handoff request), and a stored assignment currently covered by a
    // substitute leaves the teacher free at that time.
    return !events.some((t) => {
      if (t.id === ev.id) return false;
      if (!dateRangesOverlap(t.start, t.end, ev.start, ev.end)) return false;
      const staffing = resolveEffectiveEventStaffing(t);
      const coveredForUser = staffing.effectiveLeadTeacherId !== uid
        && !staffing.effectiveSupportingTeacherIds.includes(uid)
        && (String(t.mainTeacherId || '') === uid || (t.supportingTeacherIds || []).map(String).includes(uid));
      return !coveredForUser;
    });
  }, [
    currentUser?.uid,
    events,
  ]);

  const claimOpportunity = useCallback(async (ev: SubOpportunityEvent) => {
    if (!currentUser?.uid) return;
    if (!ev?.id || !ev?.squadId) return;
    setClaimError(null);
    setClaimingId(ev.id);
    try {
      await claimSubOpportunity({
        eventId: ev.id,
        squadId: ev.squadId,
        claimerTeacherId: currentUser.uid,
      });

      // Optimistic UI update: keep the STORED assignment (the absent teacher stays
      // in mainTeacherId/supportingTeacherIds, matching Firestore) and mark the
      // claim through subStatus/subAssignedTeacherId/activeCoverage so the
      // "Covering for" label renders and the request-a-sub gate stays closed.
      setSubOppEvents(prev => prev.filter(x => x.id !== ev.id));
      setEvents(prev => {
        const existingCoverage = ev.activeCoverage?.[0];
        // The chain root claiming a handoff back ends the coverage entirely:
        // the server clears activeCoverage and the sub mirror fields, so the
        // event returns to its plain stored assignment.
        const isReclaim = existingCoverage?.status === 'open'
          && existingCoverage.incumbentCoverage?.status === 'claimed'
          && String(existingCoverage.incumbentCoverage.requestedTeacherId || '') === currentUser.uid;
        const claimedCoverage = existingCoverage
          ? { ...existingCoverage, status: 'claimed' as const, substituteTeacherId: currentUser.uid }
          : {
              requestId: '',
              requestedRole: 'lead' as const,
              requestedTeacherId: String(ev.subRequestedByTeacherId || ev.mainTeacherId || ''),
              status: 'claimed' as const,
              substituteTeacherId: currentUser.uid,
            };
        const next: SquadCalendarEvent[] = [
          {
            id: ev.id,
            title: ev.title,
            start: ev.start,
            end: ev.end,
            squadId: ev.squadId,
            mainTeacherId: ev.mainTeacherId || undefined,
            supportingTeacherIds: ev.supportingTeacherIds || [],
            ...(isReclaim ? { subStatus: 'none' as const } : {
              subStatus: 'assigned' as const,
              subRequestedByTeacherId: ev.subRequestedByTeacherId ?? null,
              subRequestedByTeacherName: ev.subRequestedByTeacherName ?? null,
              subAssignedTeacherId: currentUser.uid,
              activeCoverage: [claimedCoverage],
            }),
          },
          ...prev.filter(x => x.id !== ev.id),
        ];
        next.sort((a, b) => a.start.getTime() - b.start.getTime());
        return next;
      });
    } catch (e: any) {
      console.error('Failed to claim subbing opportunity', e);
      setClaimError(e?.message || 'Failed to claim. Please try again.');
    } finally {
      setClaimingId(null);
    }
  }, [currentUser?.uid]);

  const openWeekEventActions = (ev: SquadCalendarEvent) => {
    setSelectedWeekEvent(ev);
    setWeekEventModalMode('actions');
    setWeekEventModalOpen(true);
    setSubReason('schedule_conflict');
    setSubNote('');
    setSubRequestedRole(resolveEffectiveEventStaffing(ev).effectiveLeadTeacherId === currentUser?.uid ? 'lead' : 'co_teacher');
    setSubNoteError(null);
  };

  const closeWeekEventModal = () => {
    setWeekEventModalOpen(false);
    setSelectedWeekEvent(null);
    setRequestingSub(false);
    setSubNoteError(null);
  };

  const submitWeekSubRequest = async () => {
    if (!currentUser?.uid || !selectedWeekEvent) return;
    const ev = selectedWeekEvent;
    const s = squadById.get(ev.squadId) || null;
    const title = (s?.title || ev.title || 'Class').toString();

    const trimmedNote = String(subNote || '').trim();
    if (!trimmedNote) {
      setSubNoteError(
        'Teaching notes are required. Please add notes to help the substitute do a great job and have a good experience teaching this class.'
      );
      return;
    }

    setConfirmState({
      open: true,
      title: 'Request a sub for this class?',
      message:
        'You will remain responsible for this class until a sub claims it.\n\n'
        + 'We will also mark you as unavailable during this class time.',
      confirmLabel: 'Request sub',
      onConfirm: async () => {
        setConfirmState(c => ({ ...c, open: false }));
        try {
          setRequestingSub(true);
          await requestSubForSquadEvent({
            eventId: ev.id,
            squadId: ev.squadId,
            requestedByTeacherId: currentUser.uid,
            requestedRole: subRequestedRole,
            classTitle: title,
            classStart: ev.start,
            classEnd: ev.end,
            quarterKey: quarterKeyForDateInZone(ev.start, teacherTz),
            dateKey: dateKeyForDateInZone(ev.start, teacherTz),
            availabilityTimeZone: teacherTz,
            reason: subReason,
            note: trimmedNote,
          });
          // Optimistic UI: mark event as sub-requested immediately so weekly card updates without reload.
          setEvents(prev => prev.map(x => (
            x.id === ev.id
              ? { ...x, subStatus: 'requested' as any, subRequestedByTeacherId: currentUser.uid }
              : x
          )));
          closeWeekEventModal();
        } catch (e: any) {
          console.error('Failed to request sub', e);
          setClaimError(e?.message || 'Failed to request sub. Please try again.');
        } finally {
          setRequestingSub(false);
        }
      },
    });
  };

  const cancelWeekSubRequest = async () => {
    if (!currentUser?.uid || !selectedWeekEvent) return;
    const ev = selectedWeekEvent;
    try {
      setRequestingSub(true);
      await cancelSubRequestForSquadEvent({
        eventId: ev.id,
        squadId: ev.squadId,
        requestedByTeacherId: currentUser.uid,
      });
      // Optimistic UI: clear marker on this event in our local schedule
      setEvents(prev => prev.map(x => x.id === ev.id ? { ...x, subStatus: 'none', subRequestedByTeacherId: null } : x));
      closeWeekEventModal();
    } catch (e: any) {
      console.error('Failed to cancel sub request', e);
      setClaimError(e?.message || 'Failed to cancel sub request. Please try again.');
    } finally {
      setRequestingSub(false);
    }
  };

  const goToSquad = (squadId: string, opts?: { tab?: 'roster' | 'curriculum' | 'calendar'; attendance?: boolean }) => {
    const params = new URLSearchParams();
    if (opts?.tab) params.set('tab', opts.tab);
    if (opts?.attendance) params.set('attendance', '1');
    const qs = params.toString();
    navigate(`/app/squads/${squadId}${qs ? `?${qs}` : ''}`);
  };

  const renderMetaLine = (s: Squad | null) => {
    if (!s) return null;
    const addressText = s.address || s.locationName || s.schoolName || 'Address TBD';
    const ageText = (s.startGrade || s.endGrade)
      ? `${s.startGrade || ''}${s.startGrade && s.endGrade ? '–' : ''}${s.endGrade || ''}`
      : 'TBD';
    const curriculumText = (s.curriculumId || 'music').toString();
    return (
      <div className="td-meta">
        <span className="td-meta-pill">📍 {addressText}</span>
        <span className="td-meta-pill">🧒 {ageText}</span>
        <span className="td-meta-pill">🎼 {curriculumText}</span>
      </div>
    );
  };

  const renderLargeCalendarEventContent = (eventInfo: any) => {
    const squadId = String((eventInfo.event.extendedProps as any)?.squadId || '').trim();
    const squad = squadId ? (squadById.get(squadId) || null) : null;
    const tz = String((squad as any)?.timezone || DEFAULT_TZ).trim() || DEFAULT_TZ;
    const startMs = Number((eventInfo.event.extendedProps as any)?.startMs);
    const start = Number.isFinite(startMs) ? new Date(startMs) : null;
    const isMySubRequested = !!((eventInfo.event.extendedProps as any)?.isMySubRequested);
    const isCoveredForMe = !!((eventInfo.event.extendedProps as any)?.isCoveredForMe);
    const substituteName = String((eventInfo.event.extendedProps as any)?.substituteName || 'Your substitute');
    const coveringFor = String((eventInfo.event.extendedProps as any)?.coveringFor || '');
    const title = (squad?.title || eventInfo.event.title || 'Class').toString();
    const timeLabel = start && !Number.isNaN(start.getTime?.())
      ? `${formatTimeInZone(start, tz, 'h:mm')} ${tzAbbrev(tz)}`
      : '';

    return (
      <div className="td-calendar-event-content">
        {timeLabel && <span className="td-calendar-event-time">{timeLabel}</span>}
        <span className="td-calendar-event-title">{title}</span>
        {isMySubRequested && <span className="td-calendar-event-sub-badge">Sub requested</span>}
        {isCoveredForMe && (
          <span className="td-calendar-event-covered-label" title={`${substituteName} is subbing for you`}>
            Covered · {substituteName}
          </span>
        )}
        {coveringFor && <span className="td-calendar-event-covered-label">Covering for {coveringFor}</span>}
      </div>
    );
  };

  return (
    <div className="td-root">
      <div className="td-upcoming">
        {!loading && nextClass ? (
          <div className="td-greeting">
            <span className="td-greeting-hello">Hi {firstName}!</span>{' '}
            <span className="td-greeting-next">Your next class is</span>{' '}
            <span className="td-greeting-when">{nextClassPhrase}</span>
          </div>
        ) : (
          <div className="td-section-title">Upcoming Classes</div>
        )}

        {loading ? (
          <div className="td-skeleton">Loading your schedule…</div>
        ) : !nextClass ? (
          <div className="td-empty">
            <div className="td-empty-title">No upcoming classes found</div>
            <div className="td-empty-sub">If this looks wrong, check assignments in the calendar.</div>
          </div>
        ) : (
          <div
            className={`td-boarding-pass td-boarding-pass-clickable ${isFinalWeekNextClass ? 'td-boarding-pass-final-week' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => goToSquad(nextClass.squadId)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                goToSquad(nextClass.squadId);
              }
            }}
          >
            <div className="td-boarding-left">
              <div className="td-boarding-when">
                <span className="td-when-day">{formatTimeInZone(nextClass.start, nextTz, 'EEE')}</span>
                <span className="td-when-time">
                  {formatTimeInZone(nextClass.start, nextTz, 'MMMM do')},{' '}
                  {formatTimeInZone(nextClass.start, nextTz, 'h:mm a')} {nextTzShort}
                  <span className="td-arrive-inline">
                    {' '}· Arrive {formatTimeInZone(new Date(nextClass.start.getTime() - 15 * 60 * 1000), nextTz, 'h:mm a')}
                  </span>
                </span>
              </div>
              <div className="td-boarding-title">
                <div className="td-squad-name">{nextSquad?.title || nextClass.title}</div>
              </div>
              {renderMetaLine(nextSquad)}
              {nextEnrollment && <div className={`td-enrollment td-enrollment--${nextEnrollment.tone}`}>{nextEnrollment.text}</div>}
            </div>

            <div className="td-boarding-right">
              <div className="td-boarding-corner">
                {typeof weeksLeft === 'number' && weeksLeft > 0 && (
                  <div className="td-corner-badge td-corner-badge-weeks">
                    {weeksLeft} {weeksLeft === 1 ? 'week' : 'weeks'} left
                  </div>
                )}
                {isFinalDayNextClass && (
                  <div className="td-corner-badge td-corner-badge-finalday">
                    Final day
                  </div>
                )}
              </div>
              <div className="td-actions">
                <button
                  className="td-btn td-btn-launch"
                  onClick={(e) => {
                    e.stopPropagation();
                    goToSquad(nextClass.squadId);
                  }}
                >
                  View Squad
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="td-week">
          <div className="td-week-header">
            <div className="td-section-title" style={{ margin: 0 }}>My Schedule ({weekLabel})</div>
              <div className="td-week-actions">
                <TeacherCalendarSubscription />
                <button
                  type="button"
                  className="td-link-btn"
                  onClick={() => navigate('/app/availability')}
                >
                  <svg className="td-action-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="3" y="5" width="18" height="16" rx="3" fill="none" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M7 3v4M17 3v4M3 10h18M8 15l2.2 2.2L16 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Update Availability</span>
                  {needsCurrentSeasonWeeklyAvailability && (
                    <span className="td-notif-badge" aria-label="Availability incomplete" title={`Please set your ${currentAvailabilityQuarterLabel} weekly availability`}>
                      !
                    </span>
                  )}
                </button>
              </div>
          </div>
          <div className="td-week-carousel">
            <button
              type="button"
              className="td-week-nav"
              onClick={() => setWeekOffset(v => Math.max(0, v - 1))}
              disabled={weekOffset === 0}
              aria-label="Previous week"
              title="Previous week"
            >
              ‹
            </button>

            <div className="td-week-strip" role="list" aria-label={`Week of ${weekLabel}`}>
              {groupedWeek.map(day => (
                <div key={day.dayKey} className="td-day" role="listitem">
                  <div className="td-day-header">{day.dayLabel}</div>
                  {day.items.length === 0 ? (
                    <div className="td-day-empty">—</div>
                  ) : (
                    day.items.slice(0, 5).map(ev => {
                      const s = squadById.get(ev.squadId) || null;
                      const tz = (s?.timezone || DEFAULT_TZ).toString();
                      const tzShort = tzAbbrev(tz);
                      const isMySubRequested = !!(
                        currentUser?.uid
                        && ev.subStatus === 'requested'
                        && ev.subRequestedByTeacherId === currentUser.uid
                      );
                      const isCoveredForMe = ev.subStatus === 'assigned' && ev.subRequestedByTeacherId === currentUser?.uid;
                      const claimedCoverage = ev.activeCoverage?.find(coverage => coverage.status === 'claimed' && coverage.substituteTeacherId === currentUser?.uid);
                      const coveringFor = claimedCoverage ? (teacherNamesById[claimedCoverage.requestedTeacherId] || 'the assigned teacher') : '';
                      const assignedTeacherId = String(ev.subAssignedTeacherId || ev.mainTeacherId || '').trim();
                      const substituteName = teacherNamesById[assignedTeacherId] || 'Your substitute';
                      const enrollment = enrollmentMessage(s);
                      return (
                        <button
                          key={ev.id}
                          className={`td-day-item ${isMySubRequested ? 'td-day-item-sub-requested' : ''} ${isCoveredForMe ? 'td-day-item-covered' : ''}`}
                          onClick={() => openWeekEventActions(ev)}
                          title={isMySubRequested ? 'Sub requested' : (isCoveredForMe ? `${substituteName} is subbing for you` : undefined)}
                        >
                          <div className="td-day-item-time">{formatTimeInZone(ev.start, tz, 'h:mm')} {tzShort}</div>
                          <div className="td-day-item-title">{s?.title || ev.title}</div>
                          {enrollment && (
                            <div
                              className={`td-day-item-enrollment td-day-item-enrollment--${enrollment.tone}`}
                              title={enrollment.text}
                              aria-label={enrollment.text}
                            >
                              {compactEnrollmentMessage(s)}
                            </div>
                          )}
                          {isMySubRequested && (
                            <div className="td-day-item-badge" aria-label="Sub requested">Sub requested</div>
                          )}
                          {isCoveredForMe && (
                            <div className="td-day-item-covered-label">Covered — {substituteName} is subbing for you</div>
                          )}
                          {coveringFor && <div className="td-day-item-covered-label">Covering {claimedCoverage?.requestedRole === 'co_teacher' ? 'co-teacher' : 'lead'} for {coveringFor}</div>}
                        </button>
                      );
                    })
                  )}
                </div>
              ))}
            </div>

            <button
              type="button"
              className="td-week-nav"
              onClick={() => setWeekOffset(v => Math.min(8, v + 1))}
              aria-label="Next week"
              title="Next week"
            >
              ›
            </button>
          </div>

          <div className="td-subbing">
            <div className="td-subbing-title">Available Subbing Opportunities</div>
            {loadingSubs ? (
              <div className="td-subbing-empty">Loading subbing opportunities…</div>
            ) : availableSubOpps.length === 0 ? (
              <div className="td-subbing-empty">No subbing opportunities right now.</div>
            ) : (
              <div className="td-subbing-list" role="list">
                {availableSubOpps.map((ev) => {
                  const s = ev.squadId
                    ? (squadById.get(ev.squadId) || subOppSquads[String(ev.squadId)] || null)
                    : null;
                  const tz = (s?.timezone || DEFAULT_TZ).toString();
                  const badge = subUrgencyBadge(ev.start, now, tz);
                  const canClaim = canTeacherClaimOpportunity(ev);
                  const school = (s?.schoolName || s?.locationName || '').toString().trim();
                  const subject = subjectLabelFromSquad(s);
                  const titleRight = [school, subject].filter(Boolean).join(' ');
                  const address = (s?.address || s?.locationName || s?.schoolName || 'Address TBD').toString();
                  const isRequested = ev.subStatus === 'requested';
                  const primary = `🤝 ${formatTimeInZone(ev.start, tz, 'EEE, MMM d')} · `
                    + `${formatTimeInZone(ev.start, tz, 'h:mm')}–${formatTimeInZone(ev.end, tz, 'h:mm a')}`
                    + ` — ${titleRight || (s?.title || ev.title || 'Class')}`;

                  return (
                    <div key={ev.id} className="td-subbing-item" role="listitem">
                      <div className="td-subbing-primary">
                        <div className="td-subbing-info">
                          <div className="td-subbing-primary-text">{primary}</div>
                          {isRequested && (
                            <div className="td-subbing-secondary">
                              {ev.activeCoverage?.[0]?.requestedRole === 'co_teacher' ? 'Co-teacher' : 'Lead teacher'} coverage requested by {String(ev.subRequestedByTeacherName || '').trim() || 'a teacher'}
                            </div>
                          )}
                          <div className="td-subbing-secondary">{address}</div>
                        </div>
                        <div className="td-subbing-actions">
                          {badge && <div className="td-subbing-badge" aria-label={`Urgency: ${badge}`}>{badge}</div>}
                          {canClaim && (
                            <button
                              type="button"
                              className="td-subbing-claim-btn"
                              disabled={claimingId === ev.id}
                              onClick={() => {
                                const day = formatTimeInZone(ev.start, tz, 'EEEE, MMMM d, yyyy');
                                const time = `${formatTimeInZone(ev.start, tz, 'h:mm a')}–${formatTimeInZone(ev.end, tz, 'h:mm a')} ${getTimeZoneLabel(tz)}`;
                                const classTitle = (titleRight || (s?.title || ev.title || 'Class')).toString();

                                setConfirmState({
                                  open: true,
                                  title: 'Claim this substitute opportunity?',
                                  message:
                                    'Check your calendar. Are you sure you want to claim this? Once claimed you are responsible for teaching it.'
                                    + `\n\nCLASS: ${classTitle}`
                                    + `\nDAY: ${day}`
                                    + `\nTIME: ${time}`
                                    + `\nADDRESS: ${address}`,
                                  confirmLabel: 'Claim',
                                  onConfirm: async () => {
                                    setConfirmState(c => ({ ...c, open: false }));
                                    await claimOpportunity(ev);
                                  },
                                });
                              }}
                            >
                              {claimingId === ev.id ? 'Claiming…' : 'Claim $70+/hr'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {claimError && <div className="td-subbing-error">{claimError}</div>}
          </div>

          <div className="td-calendar-wrap">
              <FullCalendar
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                timeZone={teacherTz}
                headerToolbar={{ left: 'prev,next', center: 'title', right: '' }}
                height="auto"
                nowIndicator
                fixedWeekCount={false}
                showNonCurrentDates={false}
                dayMaxEventRows={3}
                displayEventTime={false}
                events={events.map(ev => ({
                  id: ev.id,
                  title: squadById.get(ev.squadId)?.title || ev.title,
                  start: fullCalendarWallTime(ev.start, teacherTz),
                  end: fullCalendarWallTime(ev.end, teacherTz),
                  extendedProps: {
                    squadId: ev.squadId,
                    startMs: ev.start.getTime(),
                    isMySubRequested: ev.subStatus === 'requested' && ev.subRequestedByTeacherId === currentUser?.uid,
                    isCoveredForMe: ev.subStatus === 'assigned' && ev.subRequestedByTeacherId === currentUser?.uid,
                    substituteName: teacherNamesById[String(ev.subAssignedTeacherId || ev.mainTeacherId || '').trim()] || 'Your substitute',
                    coveringFor: ev.activeCoverage?.some(coverage => coverage.status === 'claimed' && coverage.substituteTeacherId === currentUser?.uid)
                      ? teacherNamesById[String(ev.activeCoverage[0].requestedTeacherId || '')] || 'the assigned teacher'
                      : '',
                  },
                }))}
                eventContent={renderLargeCalendarEventContent}
                eventClassNames={(arg) => {
                  try {
                    const sid = String((arg.event.extendedProps as any)?.squadId || '').trim();
                    const lastKey = sid ? finalDayKeyBySquadId[sid] : '';
                    const tz = sid ? String((squadById.get(sid) as any)?.timezone || DEFAULT_TZ).trim() || DEFAULT_TZ : DEFAULT_TZ;
                    const source = events.find(event => event.id === arg.event.id);
                    const classes: string[] = [];
                    if (lastKey && source?.start && dayKeyInZone(source.start, tz) === lastKey) classes.push('td-final-day-event');
                    if (source?.subStatus === 'requested' && source.subRequestedByTeacherId === currentUser?.uid) {
                      classes.push('td-calendar-event-sub-requested');
                    }
                    if (source?.subStatus === 'assigned' && source.subRequestedByTeacherId === currentUser?.uid) {
                      classes.push('td-calendar-event-covered');
                    }
                    return classes;
                  } catch (_) {}
                  return [];
                }}
                eventClick={(info) => {
                  const selected = events.find((event) => event.id === info.event.id);
                  if (selected) openWeekEventActions(selected);
                }}
              />
          </div>
        </div>
      </div>

      {weekEventModalOpen && selectedWeekEvent && (
        <div className="td-modal-overlay" onClick={closeWeekEventModal}>
          <div className="td-modal" onClick={(e) => e.stopPropagation()}>
            <div className="td-modal-title">
              {(squadById.get(selectedWeekEvent.squadId)?.title || selectedWeekEvent.title || 'Class').toString()}
            </div>
            <div className="td-modal-sub">
              {formatTimeInZone(selectedWeekEvent.start, teacherTz, 'EEE, MMM d')} ·{' '}
              {formatTimeInZone(selectedWeekEvent.start, teacherTz, 'h:mm a')}–{formatTimeInZone(selectedWeekEvent.end, teacherTz, 'h:mm a')} {teacherTzLabel}
            </div>

            {selectedWeekEvent.subStatus === 'assigned' && selectedWeekEvent.subRequestedByTeacherId === currentUser?.uid && (
              <div className="td-modal-covered-status">
                Covered — {teacherNamesById[String(selectedWeekEvent.subAssignedTeacherId || selectedWeekEvent.mainTeacherId || '').trim()] || 'Your substitute'} is subbing for you
              </div>
            )}

            {weekEventModalMode === 'actions' ? (
              <div className="td-modal-actions">
                <button
                  type="button"
                  className="td-btn td-btn-launch"
                  onClick={() => { closeWeekEventModal(); goToSquad(selectedWeekEvent.squadId); }}
                >
                  View Squad
                </button>
                {selectedWeekEvent.subStatus === 'assigned' && selectedWeekEvent.subRequestedByTeacherId === currentUser?.uid ? null : selectedWeekEvent.subStatus === 'requested' && selectedWeekEvent.subRequestedByTeacherId === currentUser?.uid ? (
                  <button
                    type="button"
                    className="td-btn td-btn-primary"
                    onClick={() => {
                      setConfirmState({
                        open: true,
                        title: 'Cancel sub request?',
                        message:
                          'This will remove the sub request and the class will no longer appear to other teachers as a sub opportunity.\n\n'
                          + 'It will also restore your availability during this class time (removing the unavailability created when you requested the sub).',
                        confirmLabel: 'Cancel sub request',
                        onConfirm: async () => {
                          setConfirmState(c => ({ ...c, open: false }));
                          await cancelWeekSubRequest();
                        },
                      });
                    }}
                  >
                    Cancel sub request
                  </button>
                ) : selectedWeekEvent.subStatus === 'requested' ? (
                  <button type="button" className="td-btn td-btn-primary" disabled title="A sub has already been requested for this class.">
                    Sub already requested
                  </button>
                ) : (() => {
                  const staffing = resolveEffectiveEventStaffing(selectedWeekEvent);
                  return staffing.effectiveLeadTeacherId === currentUser?.uid || staffing.effectiveSupportingTeacherIds.includes(String(currentUser?.uid || ''));
                })() ? (
                  <button
                    type="button"
                    className="td-btn td-btn-primary"
                    onClick={() => {
                      // Before we collect reason/teaching notes, require logistics to be filled out (or explicitly marked unknown).
                      setLogisticsModalOpen(true);
                    }}
                  >
                    Request a sub
                  </button>
                ) : null}
                <button type="button" className="td-btn" onClick={closeWeekEventModal}>
                  Cancel
                </button>
              </div>
            ) : (
              <>
                <div className="td-modal-form">
                  {resolveEffectiveEventStaffing(selectedWeekEvent).effectiveLeadTeacherId === currentUser?.uid && resolveEffectiveEventStaffing(selectedWeekEvent).effectiveSupportingTeacherIds.includes(String(currentUser?.uid || '')) && (
                    <label className="td-modal-label">
                      Role needing coverage
                      <select className="td-modal-select" value={subRequestedRole} onChange={(e) => setSubRequestedRole(e.target.value as 'lead' | 'co_teacher')}>
                        <option value="lead">Lead teacher</option>
                        <option value="co_teacher">Co-teacher</option>
                      </select>
                    </label>
                  )}
                  <label className="td-modal-label">
                    Reason
                    <select className="td-modal-select" value={subReason} onChange={(e) => setSubReason(e.target.value as SubRequestReason)}>
                      <option value="schedule_conflict">Schedule conflict</option>
                      <option value="illness">Illness</option>
                      <option value="travel">Travel</option>
                      <option value="other">Other</option>
                    </select>
                  </label>
                  <label className="td-modal-label">
                    Teaching notes (required)
                    <div className="td-modal-help">
                      These notes are meant to help your substitute do a great job and have a good experience teaching. Include what’s worked with
                      challenging behaviors, what each student is like, classroom routines, and anything else they should know coming in.
                    </div>
                    <textarea
                      className="td-modal-textarea"
                      rows={7}
                      value={subNote}
                      onChange={(e) => {
                        setSubNote(e.target.value);
                        if (subNoteError) setSubNoteError(null);
                      }}
                      placeholder={
                        "Examples to include:\n"
                        + "- Student behavior notes + what’s worked\n"
                        + "- Helpful routines / expectations\n"
                        + "- Seating / grouping tips\n"
                        + "- Any safety or pickup/dropoff nuances"
                      }
                    />
                    {subNoteError && <div className="td-modal-error" role="alert">{subNoteError}</div>}
                  </label>
                </div>
                <div className="td-modal-actions">
                  <button type="button" className="td-btn" onClick={() => setWeekEventModalMode('actions')} disabled={requestingSub}>
                    Back
                  </button>
                  <button type="button" className="td-btn td-btn-primary" onClick={submitWeekSubRequest} disabled={requestingSub}>
                    {requestingSub ? 'Requesting…' : 'Request sub'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmState.open}
        onClose={() => setConfirmState(s => ({ ...s, open: false }))}
        onConfirm={() => confirmState.onConfirm()}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmLabel || 'Confirm'}
        cancelText="Cancel"
      />

      {selectedWeekEvent && (
        <RequiredTeacherLogisticsModal
          isOpen={logisticsModalOpen}
          squadId={selectedWeekEvent.squadId}
          currentUserId={currentUser?.uid}
          onClose={() => setLogisticsModalOpen(false)}
          onContinue={() => {
            setLogisticsModalOpen(false);
            setWeekEventModalMode('requestSub');
          }}
        />
      )}
    </div>
  );
};

export default TeacherDashboard;
